export type RoutePoint = {
    lat: number;
    lng: number;
};

export type NeighborDistanceWarning = {
    fromIndex: number;
    toIndex: number;
    distanceKm: number;
    level: 'yellow' | 'red';
};

export type RouteWarningsResult = {
    neighborWarnings: NeighborDistanceWarning[];
    sequenceWarning: boolean;
    totalDistanceKm: number;
    /** Rough walking time in minutes (distance @ 4 km/h + 8 min per stop). */
    estimatedMinutes: number;
    nearestNeighborDistanceKm: number;
};

const EARTH_RADIUS_KM = 6371;
const WALK_KMH = 4;
const MINUTES_PER_STOP = 8;
const YELLOW_KM = 1;
const RED_KM = 2;
const SEQUENCE_RATIO = 1.25;
const TURNBACK_DEG = 135;

/** Haversine distance in kilometres. */
export const haversineKm = (a: RoutePoint, b: RoutePoint): number => {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

    return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
};

const bearingDeg = (a: RoutePoint, b: RoutePoint): number => {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const toDeg = (rad: number) => (rad * 180) / Math.PI;
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const dLng = toRad(b.lng - a.lng);
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

    return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const angleDiffDeg = (a: number, b: number): number => {
    const d = Math.abs(a - b) % 360;

    return d > 180 ? 360 - d : d;
};

/** Greedy nearest-neighbor tour length starting at index 0. */
export const nearestNeighborLengthKm = (points: RoutePoint[]): number => {
    if (points.length < 2) return 0;

    const remaining = new Set(points.map((_, i) => i));
    let current = 0;
    remaining.delete(0);
    let total = 0;

    while (remaining.size) {
        let best = -1;
        let bestDist = Infinity;

        remaining.forEach((idx) => {
            const d = haversineKm(points[current], points[idx]);

            if (d < bestDist) {
                bestDist = d;
                best = idx;
            }
        });

        total += bestDist;
        current = best;
        remaining.delete(best);
    }

    return total;
};

export const analyzeRoute = (points: RoutePoint[]): RouteWarningsResult => {
    const neighborWarnings: NeighborDistanceWarning[] = [];
    let totalDistanceKm = 0;

    for (let i = 0; i < points.length - 1; i++) {
        const distanceKm = haversineKm(points[i], points[i + 1]);

        totalDistanceKm += distanceKm;

        if (distanceKm > RED_KM) {
            neighborWarnings.push({
                fromIndex: i,
                toIndex: i + 1,
                distanceKm,
                level: 'red',
            });
        } else if (distanceKm > YELLOW_KM) {
            neighborWarnings.push({
                fromIndex: i,
                toIndex: i + 1,
                distanceKm,
                level: 'yellow',
            });
        }
    }

    const nearestNeighborDistanceKm = nearestNeighborLengthKm(points);
    let sequenceWarning = false;

    if (points.length >= 3 && nearestNeighborDistanceKm > 0) {
        if (totalDistanceKm / nearestNeighborDistanceKm >= SEQUENCE_RATIO) {
            sequenceWarning = true;
        }
    }

    if (!sequenceWarning && points.length >= 3) {
        for (let i = 0; i < points.length - 2; i++) {
            const b1 = bearingDeg(points[i], points[i + 1]);
            const b2 = bearingDeg(points[i + 1], points[i + 2]);

            if (angleDiffDeg(b1, b2) > TURNBACK_DEG) {
                sequenceWarning = true;
                break;
            }
        }
    }

    const estimatedMinutes = Math.round(
        (totalDistanceKm / WALK_KMH) * 60 + points.length * MINUTES_PER_STOP,
    );

    return {
        neighborWarnings,
        sequenceWarning,
        totalDistanceKm,
        estimatedMinutes,
        nearestNeighborDistanceKm,
    };
};

/** Soft guidance for story length (not blocking). */
export const ROUTE_SOFT_LIMITS = {
    minSteps: 6,
    maxSteps: 12,
    minTotalKm: 2,
    maxTotalKm: 4,
};
